/**
 * Config API functions for projects, file types, and mountains.
 */

import apiClient, { getVeToken } from './client';
import type {
  Project,
  FileType,
  Mountain,
  MountainCreate,
  FieldOptions,
  MountainVehicleTypesResponse,
  SoftwareSupplier,
  SoftwareSupplierCreate,
  VehicleTypeSource,
} from '@/types';

// Projects
export const getProjects = async (): Promise<Project[]> => {
  const response = await apiClient.get<{ results: Project[] }>('/projects/');
  return response.data.results;
};

export const getProject = async (id: string): Promise<Project> => {
  const response = await apiClient.get<Project>(`/projects/${id}/`);
  return response.data;
};

export const createProject = async (data: { name: string; code: string; description?: string }): Promise<Project> => {
  const response = await apiClient.post<Project>('/projects/', data);
  return response.data;
};

export const updateProject = async (
  id: string,
  data: { name?: string; code?: string; description?: string }
): Promise<Project> => {
  const response = await apiClient.patch<Project>(`/projects/${id}/`, data);
  return response.data;
};

export const deleteProject = async (id: string): Promise<void> => {
  await apiClient.delete(`/projects/${id}/`);
};

// File Types
export const getFileTypes = async (): Promise<FileType[]> => {
  const response = await apiClient.get<{ results: FileType[] }>('/file-types/');
  return response.data.results;
};

export const getFileType = async (id: string): Promise<FileType> => {
  const response = await apiClient.get<FileType>(`/file-types/${id}/`);
  return response.data;
};

export const createFileType = async (data: { name: string; code: string }): Promise<FileType> => {
  const response = await apiClient.post<FileType>('/file-types/', data);
  return response.data;
};

export const updateFileType = async (
  id: string,
  data: { name?: string; code?: string }
): Promise<FileType> => {
  const response = await apiClient.patch<FileType>(`/file-types/${id}/`, data);
  return response.data;
};

export const deleteFileType = async (id: string): Promise<void> => {
  await apiClient.delete(`/file-types/${id}/`);
};

// Mountains
export const getMountains = async (search?: string): Promise<Mountain[]> => {
  const params = search ? { search } : {};
  const response = await apiClient.get<Mountain[]>('/mountains/', { params });
  return Array.isArray(response.data) ? response.data : (response.data as { results: Mountain[] }).results ?? [];
};

export const createMountain = async (data: MountainCreate): Promise<Mountain> => {
  const response = await apiClient.post<Mountain>('/mountains/', data);
  return response.data;
};

export const updateMountain = async (id: number, data: MountainCreate): Promise<Mountain> => {
  const response = await apiClient.put<Mountain>(`/mountains/${id}/`, data);
  return response.data;
};

export const deleteMountain = async (id: number): Promise<void> => {
  await apiClient.delete(`/mountains/${id}/`);
};

// Software suppliers (底软供应商)
export const getSoftwareSuppliers = async (): Promise<SoftwareSupplier[]> => {
  const response = await apiClient.get<SoftwareSupplier[]>('/software-suppliers/');
  return Array.isArray(response.data)
    ? response.data
    : (response.data as { results: SoftwareSupplier[] }).results ?? [];
};

export const createSoftwareSupplier = async (
  data: SoftwareSupplierCreate,
): Promise<SoftwareSupplier> => {
  const response = await apiClient.post<SoftwareSupplier>('/software-suppliers/', data);
  return response.data;
};

/**
 * 查询山头下可选车型与子车型。Keycloak token 由前端随请求回传给后端，后端据此以
 * 当前登录用户身份访问 VE。
 *
 * 占用信息只作提示、不按硬件平台过滤（跨平台的同一组合并不冲突，但用户想知道谁在用），
 * 因此这里不需要传硬件平台；真正的冲突判定由后端在保存时按三元组负责。
 *
 * @param projectId 编辑中的项目 ID：后端会把自己的车型从「已被占用」中排除
 * @param source 数据源：默认 lineage（VE 谱系接口，只含新车型）；
 *               yaml 走 VE 老车型接口，补齐谱系里查不到的老车型
 */
export const getMountainVehicleTypes = async (
  mountainName: string,
  projectId?: number,
  source?: VehicleTypeSource,
): Promise<MountainVehicleTypesResponse> => {
  const response = await apiClient.get<MountainVehicleTypesResponse>(
    '/mountains/vehicle-types/',
    {
      params: {
        mountain_name: mountainName,
        ...(projectId != null ? { project_id: projectId } : {}),
        ...(source ? { source } : {}),
      },
      headers: { 'X-VE-Token': getVeToken() ?? '' },
    },
  );
  return response.data;
};

export const listProjectSpaceProjects = async (): Promise<{ code: number; data: import('@/types').MainlineProject[] }> => {
  const response = await apiClient.get('/projects/space-list/');
  return response.data;
};

export const getFieldOptions = async (): Promise<FieldOptions> => {
  const response = await apiClient.get<{ code: number; data: FieldOptions }>('/projects/field-options/');
  return response.data.data;
};

export interface ProjectBriefOption {
  id: number;
  name: string;
  mountain: { id: number; name: string } | null;
}

export const getAllProjectsBrief = async (): Promise<ProjectBriefOption[]> => {
  const response = await apiClient.get<{ projects: ProjectBriefOption[] }>('/projects/bvp-projects/');
  return response.data.projects || [];
};
